/**
 * 기기 신뢰 — 이 기기의 키와 증명 (서버 server/services/deviceTrust.js · Rust src-tauri/src/device_key.rs)
 *
 * 서버는 PC 앱과 브라우저를 구분할 수 없다. 그래서 "승인된 기기의 개인키를 가졌는가"만 묻고,
 * 로그인·재개·갱신마다 서버 nonce에 이 기기 키로 서명해 보낸다.
 *
 * - PC 앱: Rust가 키를 만들고 DPAPI로 보관한다(invoke). 여기서 키를 만지지 않는다 —
 *   WebView2 IndexedDB에 두면 프로필 폴더를 복사하는 것만으로 키가 다른 PC로 간다.
 * - 웹/PWA: WebCrypto ECDSA P-256(extractable:false) 키를 IndexedDB에 둔다. 개인키는 JS로도 꺼낼 수 없다.
 *   ⚠ 로그아웃해도 지우지 않는다(지우면 승인받은 기기가 새 기기가 되어 다시 승인을 받아야 한다).
 *   ⚠ iOS는 Safari 탭과 홈 화면 앱의 저장소가 다르다 — 요청은 홈 화면 앱에서만(canRequestHere).
 *
 * 판정은 언제나 서버다. 이 모듈이 null을 돌려주면 device 없이 보내고 서버가 거부 여부를 정한다.
 */
import { isTauri } from '../utils/tauri';
import { IS_COARSE_POINTER } from '../utils/pointer';
import { isIOS, isStandalone } from '../utils/pushSupport';
import { SERVER_BASE_URL } from '../utils/serverUrl';
import { buildProofMessage, bytesToBase64Url, ProofPurpose } from '../utils/deviceProof';
import { getDeviceId } from './deviceService';

export type DeviceKind = 'desktop' | 'web';

export interface DeviceIdentity {
  kind: DeviceKind;
  /** SPKI DER base64url */
  spki: string;
  /** base64url(SHA-256(SPKI)) */
  keyFp: string;
  computerName?: string;
}

/** 서버로 보내는 device 블록 (POST /api/login · /auth/device-resume · /auth/refresh · /devices/this) */
export interface DeviceBlock {
  kind: DeviceKind;
  keyFp: string;
  spki: string;
  nonce: string;
  sig: string;
  label?: string;
  platform?: Record<string, unknown>;
  computerName?: string;
  legacyDeviceId?: string;
  requestApproval?: boolean;
}

/** 서버가 알려 주는 이 기기 상태 (응답 device 필드) */
export interface DeviceStatusInfo {
  id: number;
  kind: DeviceKind;
  status: 'pending' | 'approved' | 'rejected' | 'revoked' | 'expired';
  fingerprint: string;
  /** 대기 중일 때만 — 관리자에게 알려 줄 6자리(이 기기에서만 보인다) */
  code?: string;
  selfConfirmed: boolean;
  requestedAt?: string;
}

export type DeviceTrustMode = 'off' | 'observe' | 'enforce';

// ─── 웹: WebCrypto + IndexedDB ───────────────────────────────────────────────

const DB_NAME = 'ina-device';
const STORE = 'keys';
const RECORD_ID = 'v1';

interface StoredKey {
  privateKey: CryptoKey;
  spki: string;
  keyFp: string;
  createdAt: number;
}

function webCryptoAvailable(): boolean {
  return typeof window !== 'undefined'
    && window.isSecureContext === true
    && !!window.crypto?.subtle
    && typeof indexedDB !== 'undefined';
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error('indexedDB blocked'));
  });
}

function idbGet(db: IDBDatabase): Promise<StoredKey | undefined> {
  return new Promise((resolve, reject) => {
    const req = db.transaction(STORE, 'readonly').objectStore(STORE).get(RECORD_ID);
    req.onsuccess = () => resolve(req.result as StoredKey | undefined);
    req.onerror = () => reject(req.error);
  });
}

function idbPut(db: IDBDatabase, value: StoredKey): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(value, RECORD_ID);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

async function sha256B64Url(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return bytesToBase64Url(new Uint8Array(digest));
}

let webKeyPromise: Promise<StoredKey | null> | null = null;

/** 웹 기기 키를 읽거나(없으면) 만든다. 실패하면 null(개인정보 보호 모드·보안 컨텍스트 아님 등). */
function loadOrCreateWebKey(): Promise<StoredKey | null> {
  if (webKeyPromise) return webKeyPromise;
  webKeyPromise = (async () => {
    if (!webCryptoAvailable()) return null;
    try {
      const db = await openDb();
      const existing = await idbGet(db);
      if (existing?.privateKey && existing.spki && existing.keyFp) return existing;
      const pair = await crypto.subtle.generateKey({ name: 'ECDSA', namedCurve: 'P-256' }, false, ['sign', 'verify']);
      // 공개키는 extractable:false여도 항상 내보낼 수 있다(WebCrypto 규격)
      const spkiBuf = await crypto.subtle.exportKey('spki', pair.publicKey);
      const record: StoredKey = {
        privateKey: pair.privateKey,
        spki: bytesToBase64Url(new Uint8Array(spkiBuf)),
        keyFp: await sha256B64Url(spkiBuf),
        createdAt: Date.now(),
      };
      await idbPut(db, record);
      // 저장소 축출을 줄인다(설치한 PWA는 대개 허용된다). 결과와 무관하게 진행
      try { await navigator.storage?.persist?.(); } catch { /* 무시 */ }
      return record;
    } catch (err) {
      console.warn('[deviceIdentity] web key unavailable:', err);
      webKeyPromise = null; // 다음 호출에서 다시 시도
      return null;
    }
  })();
  return webKeyPromise;
}

// ─── 공용 ────────────────────────────────────────────────────────────────────

let desktopInfoPromise: Promise<DeviceIdentity | null> | null = null;

/** 이 기기의 공개 정보. 키를 만들 수 없으면 null. */
export async function getDeviceIdentity(): Promise<DeviceIdentity | null> {
  if (isTauri()) {
    if (!desktopInfoPromise) {
      desktopInfoPromise = (async () => {
        try {
          const { invoke } = await import('@tauri-apps/api/core');
          const info = await invoke<{ spki: string; keyFp: string; computerName: string }>('device_key_info');
          return { kind: 'desktop' as const, spki: info.spki, keyFp: info.keyFp, computerName: info.computerName };
        } catch (err) {
          // 구버전 Rust(커맨드 없음)·비 Windows — 키 없이 보내면 서버가 APP_UPDATE_REQUIRED로 판정한다
          console.warn('[deviceIdentity] desktop key unavailable:', err);
          desktopInfoPromise = null;
          return null;
        }
      })();
    }
    return desktopInfoPromise;
  }
  const key = await loadOrCreateWebKey();
  return key ? { kind: 'web', spki: key.spki, keyFp: key.keyFp } : null;
}

/** 서버 nonce에 대한 서명(base64url). 실패하면 null. */
export async function signProof(
  purpose: ProofPurpose,
  nonce: string,
  affiliateId: string,
  userid: string
): Promise<string | null> {
  try {
    if (isTauri()) {
      const { invoke } = await import('@tauri-apps/api/core');
      // 정형 메시지는 Rust가 스스로 조립한다(임의 데이터 서명 금지)
      return await invoke<string>('device_sign_proof', { purpose, nonce, affiliateId, userid });
    }
    const key = await loadOrCreateWebKey();
    if (!key) return null;
    const message = buildProofMessage({ purpose, nonce, affiliateId, userid, keyFp: key.keyFp });
    if (!message) return null;
    const sig = await crypto.subtle.sign({ name: 'ECDSA', hash: 'SHA-256' }, key.privateKey, new TextEncoder().encode(message));
    return bytesToBase64Url(new Uint8Array(sig)); // WebCrypto ECDSA는 IEEE-P1363(r‖s) — 서버 형식과 같다
  } catch (err) {
    console.warn('[deviceIdentity] sign failed:', err);
    return null;
  }
}

/** 서버 nonce 발급(무인증). mode를 함께 받는다. 실패하면 null. */
export async function fetchChallenge(): Promise<{ nonce: string; mode: DeviceTrustMode } | null> {
  try {
    const res = await fetch(`${SERVER_BASE_URL}/api/auth/device-challenge`, { method: 'POST' });
    if (!res.ok) return null; // 구 서버(404) — 기기 신뢰 없음
    const data = await res.json();
    return typeof data?.nonce === 'string' ? { nonce: data.nonce, mode: data.mode || 'off' } : null;
  } catch {
    return null;
  }
}

/** 관리자 화면에 뜨는 자기 신고 정보 — 신뢰 근거가 아니라 관리자가 기기를 알아보는 단서다 */
export function getPlatformInfo(): Record<string, unknown> {
  const nav = navigator as Navigator & { userAgentData?: { platform?: string; mobile?: boolean } };
  return {
    ua: nav.userAgent,
    platform: nav.userAgentData?.platform || nav.platform || '',
    mobile: typeof nav.userAgentData?.mobile === 'boolean' ? nav.userAgentData.mobile : IS_COARSE_POINTER,
    standalone: isStandalone(),
    screenW: window.screen?.width,
    screenH: window.screen?.height,
    touchPoints: nav.maxTouchPoints || 0,
    tz: (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return ''; } })(),
  };
}

/**
 * 서명까지 마친 device 블록. 키를 못 만들거나 서버가 nonce를 못 주면 null —
 * 호출부는 device 없이 보내고, 서버가 정책에 따라 판정한다(구 서버면 그대로 로그인).
 * 정책이 꺼져 있으면 { block: null, mode: 'off' }(실패가 아니다 — 보낼 필요가 없을 뿐).
 */
export async function buildDeviceBlock(
  purpose: ProofPurpose,
  affiliateId: string,
  userid: string,
  extra: { requestApproval?: boolean; label?: string } = {}
): Promise<{ block: DeviceBlock | null; mode: DeviceTrustMode } | null> {
  if (!affiliateId || !userid) return null;
  const challenge = await fetchChallenge();
  if (!challenge) return null;
  // 정책이 꺼져 있으면 서버가 device 블록을 보지 않는다 — 키를 만들거나 서명하지 않는다
  // (PC 앱의 첫 키 생성도 정책이 켜진 뒤 첫 로그인으로 미뤄진다)
  if (challenge.mode === 'off') return { block: null, mode: 'off' };
  const identity = await getDeviceIdentity();
  if (!identity) return null;
  const sig = await signProof(purpose, challenge.nonce, affiliateId, userid);
  if (!sig) return null;
  const block: DeviceBlock = {
    kind: identity.kind,
    keyFp: identity.keyFp,
    spki: identity.spki,
    nonce: challenge.nonce,
    sig,
    platform: getPlatformInfo(),
  };
  if (identity.kind === 'desktop') {
    if (identity.computerName) block.computerName = identity.computerName;
    const legacy = await getDeviceId().catch(() => null);
    if (legacy) block.legacyDeviceId = legacy;
  }
  if (extra.requestApproval) {
    block.requestApproval = true;
    block.label = (extra.label || '').trim().slice(0, 40);
  }
  return { block, mode: challenge.mode };
}

/**
 * 이 화면에서 기기 승인 요청을 받을 수 있는가 (안내용 — 판정은 서버).
 * - PC 웹 브라우저: 받지 않는다(PC는 PC 앱을 쓴다). iPad+트랙패드는 pointer:fine이라 maxTouchPoints로 가른다
 * - iOS Safari 탭: 저장소가 홈 화면 앱과 달라 요청해도 앱에서 쓸 수 없다 → 홈 화면 앱에서 요청
 * - 키를 만들 수 없는 브라우저(개인정보 보호 모드 등)
 */
export function canRequestHere(): { ok: true } | { ok: false; reason: 'desktop-browser' | 'ios-browser' | 'unsupported' } {
  if (isTauri()) return { ok: false, reason: 'desktop-browser' };
  if (!webCryptoAvailable()) return { ok: false, reason: 'unsupported' };
  const touch = IS_COARSE_POINTER || (navigator.maxTouchPoints || 0) > 0;
  if (!touch) return { ok: false, reason: 'desktop-browser' };
  if (isIOS() && !isStandalone()) return { ok: false, reason: 'ios-browser' };
  return { ok: true };
}

/** 기본 기기 이름 제안 — 사용자가 고칠 수 있다 */
export function suggestDeviceLabel(): string {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return '아이폰';
  if (/iPad/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) return '아이패드';
  if (/Android/.test(ua)) return /Mobile/.test(ua) ? '안드로이드 폰' : '안드로이드 태블릿';
  return '모바일 기기';
}

// ─── 재개 신원 (토큰이 만료된 뒤 기기 키로 세션을 다시 열 때 누구인지) ───────────────
// ⚠ 직접 로그아웃(confirmLogout)하면 지운다 — 자동 로그인 비밀번호 정책과 같다.
//   401·만료로 로그아웃될 때는 지우지 않는다(그래야 재개가 된다).
//   resumeSecret은 서버가 비밀번호 로그인 때 준 재개 비밀값이다 — 기기 키만으로는 재개되지 않는다(감사 C9).
//   서버는 직접 로그아웃(POST /api/auth/logout)과 비밀번호 변경 때 이 값을 무효로 만든다.

const RESUME_KEY = 'deviceResume';

export interface ResumeIdentity { affiliateId: string; userid: string; resumeSecret?: string }

export function saveResumeIdentity(v: ResumeIdentity): void {
  try { localStorage.setItem(RESUME_KEY, JSON.stringify(v)); } catch { /* 무시 */ }
}

export function loadResumeIdentity(): ResumeIdentity | null {
  try {
    const v = JSON.parse(localStorage.getItem(RESUME_KEY) || 'null');
    if (!v || typeof v.affiliateId !== 'string' || typeof v.userid !== 'string') return null;
    return {
      affiliateId: v.affiliateId,
      userid: v.userid,
      ...(typeof v.resumeSecret === 'string' && v.resumeSecret ? { resumeSecret: v.resumeSecret } : {}),
    };
  } catch {
    return null;
  }
}

export function clearResumeIdentity(): void {
  try { localStorage.removeItem(RESUME_KEY); } catch { /* 무시 */ }
}
